import { User as FirebaseUser } from 'firebase/auth';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import { db } from './firebase';
import { AppUser, UserType } from '../types';
import { logGlobalError } from '../utils/globalErrorLogger';

export interface UserRoles {
  userType: UserType;
  isAdmin: boolean;
  canAccessApp: boolean;
}

const USERS = 'users';

function toDate(value: unknown): Date {
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  return new Date(0);
}

function mapUser(id: string, data: Record<string, any>): AppUser {
  const userType = normalizeUserType(data.userType, data.isAdmin);
  return {
    uid: data.uid ?? id,
    email: data.email ?? null,
    displayName: data.displayName ?? null,
    createdAt: toDate(data.createdAt),
    lastLoginAt: toDate(data.lastLoginAt),
    userType,
    isAdmin: userType === 'admin',
  };
}

export function normalizeUserType(value: unknown, legacyIsAdmin?: unknown): UserType {
  if (value === 'admin' || value === 'sales' || value === 'unknown') return value;
  if (legacyIsAdmin === true) return 'admin';
  return 'unknown';
}

export async function fetchUsers(): Promise<AppUser[]> {
  const snap = await getDocs(query(collection(db, USERS), orderBy('displayName', 'asc')));
  return snap.docs.map((d) => mapUser(d.id, d.data()));
}

export async function updateUserType(uid: string, userType: UserType): Promise<void> {
  await updateDoc(doc(db, USERS, uid), { userType });
}

export async function getUserProfile(uid: string): Promise<AppUser | null> {
  const snap = await getDoc(doc(db, USERS, uid));
  if (!snap.exists()) return null;
  return mapUser(snap.id, snap.data());
}

export async function bootstrapUser(user: FirebaseUser): Promise<UserRoles> {
  const ref = doc(db, USERS, user.uid);
  const snap = await getDoc(ref);

  if (!snap.exists()) {
    void setDoc(ref, {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      userType: 'unknown',
      createdAt: serverTimestamp(),
      lastLoginAt: serverTimestamp(),
    }).catch((err) =>
      logGlobalError(err, {
        context: 'users.bootstrapUser.createUserDoc',
        metadata: { uid: user.uid },
      }),
    );
    return { userType: 'unknown', isAdmin: false, canAccessApp: false };
  }

  const data = snap.data();
  const userType = normalizeUserType(data.userType, data.isAdmin);
  void setDoc(
    ref,
    {
      email: user.email,
      displayName: user.displayName,
      lastLoginAt: serverTimestamp(),
    },
    { merge: true },
  ).catch((err) =>
    logGlobalError(err, {
      context: 'users.bootstrapUser.refreshUserDoc',
      metadata: { uid: user.uid },
    }),
  );

  return {
    userType,
    isAdmin: userType === 'admin',
    canAccessApp: userType === 'admin' || userType === 'sales',
  };
}
